<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Custom Tools</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Tools created and managed by your organization
        </p>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-48">
          <Input v-model="searchQuery" placeholder="Search..." class="w-full" />
        </div>
        <Button variant="primary" size="sm" @click="emit('create')">New Tool</Button>
      </div>
    </div>

    <div v-if="store.isLoadingCustomTools" class="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" />
    </div>

    <Alert v-else-if="store.customToolsError" variant="error">
      {{ store.customToolsError }}
    </Alert>

    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else-if="filteredTools.length === 0"
      class="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700"
    >
      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{ searchQuery ? 'No tools match your search' : 'No custom tools yet' }}
      </p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <ToolTile
        v-for="tool in filteredTools"
        :key="tool.id"
        :tool="tool"
        :is-base="false"
        :is-enabled="true"
        @edit="emit('edit', tool)"
        @delete="emit('delete', tool)"
        @view-detail="emit('view-detail', tool)"
        @builds="emit('builds', tool)"
        @versions="emit('versions', tool)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { Alert, Button, Input, LoadingSpinner } from '@asabdev/asab-ui';
  import { useMcpToolsStore } from '@/stores/mcpTools';
  import type { CustomTool } from '@/lib/mcp/types';
  import ToolTile from './ToolTile.vue';

  interface Emits {
    (e: 'create'): void;
    (e: 'edit', tool: CustomTool): void;
    (e: 'delete', tool: CustomTool): void;
    (e: 'view-detail', tool: CustomTool): void;
    (e: 'builds', tool: CustomTool): void;
    (e: 'versions', tool: CustomTool): void;
  }

  const emit = defineEmits<Emits>();
  const store = useMcpToolsStore();
  const searchQuery = ref('');

  const filteredTools = computed(() => {
    const query = searchQuery.value.toLowerCase().trim();
    if (!query) return store.customTools;
    return store.customTools.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query),
    );
  });
</script>
